/**
 * 커스텀 Modal – alert / confirm 대체.
 * 전역에서 사용: Modal.alert(message), Modal.confirm(message).
 * 스타일: resources/scss/home/components/_dpr-modal.scss (인라인 스타일 미사용).
 */

const CONTAINER_ID = "dpr-modal-root";
const BACKDROP_CLASS = "dpr-modal-backdrop";
const BOX_CLASS = "dpr-modal-box";
const HEADER_CLASS = "dpr-modal-header";
const TITLE_CLASS = "dpr-modal-title";
const CLOSE_CLASS = "dpr-modal-close";
const BODY_CLASS = "dpr-modal-body";
const FOOTER_CLASS = "dpr-modal-footer";
const BTN_CLASS = "dpr-modal-btn";
const BTN_PRIMARY_CLASS = "dpr-modal-btn--primary";

function getModalLabel(key: "ok" | "cancel" | "close" | "alert_title" | "confirm_title"): string {
  const attr = document.documentElement.getAttribute(`data-modal-${key === "alert_title" ? "alert-title" : key === "confirm_title" ? "confirm-title" : key}`);
  if (attr) return attr;
  const fallback: Record<string, string> = {
    ok: "확인",
    cancel: "취소",
    close: "닫기",
    alert_title: "알림",
    confirm_title: "확인",
  };
  return fallback[key] ?? "";
}

function getContainer(): HTMLDivElement {
  let root = document.getElementById(CONTAINER_ID) as HTMLDivElement | null;
  if (!root) {
    root = document.createElement("div");
    root.id = CONTAINER_ID;
    document.body.appendChild(root);
  }
  return root;
}

function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

/** 알림 한 개만 띄우고 확인 버튼으로 닫기 */
export function alert(message: string, title?: string): Promise<void> {
  const defaultTitle = getModalLabel("alert_title") || "알림";
  const okLabel = getModalLabel("ok") || "확인";
  const closeLabel = getModalLabel("close") || "닫기";
  const titleText = title ?? defaultTitle;

  const root = getContainer();
  const backdrop = document.createElement("div");
  backdrop.className = BACKDROP_CLASS;

  const box = document.createElement("div");
  box.className = BOX_CLASS;
  box.innerHTML = `
    <div class="${HEADER_CLASS}">
      <h2 class="${TITLE_CLASS}">${escapeHtml(titleText)}</h2>
      <button type="button" class="${CLOSE_CLASS}" data-dpr-modal-close aria-label="${escapeHtml(closeLabel)}">&times;</button>
    </div>
    <div class="${BODY_CLASS}">${escapeHtml(message)}</div>
    <div class="${FOOTER_CLASS}">
      <button type="button" class="${BTN_CLASS} ${BTN_PRIMARY_CLASS}" data-dpr-modal-ok>${escapeHtml(okLabel)}</button>
    </div>
  `;

  backdrop.appendChild(box);
  root.appendChild(backdrop);

  return new Promise((resolve) => {
    const close = (): void => {
      backdrop.remove();
      resolve();
    };
    box.querySelector("[data-dpr-modal-ok]")?.addEventListener("click", close);
    box.querySelector("[data-dpr-modal-close]")?.addEventListener("click", close);
    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop) close();
    });
    box.addEventListener("click", (e) => e.stopPropagation());
  });
}

/** 확인/취소 선택, 확인 true / 취소 false */
export function confirm(message: string, title?: string): Promise<boolean> {
  const defaultTitle = getModalLabel("confirm_title") || "확인";
  const okLabel = getModalLabel("ok") || "확인";
  const cancelLabel = getModalLabel("cancel") || "취소";
  const closeLabel = getModalLabel("close") || "닫기";
  const titleText = title ?? defaultTitle;

  const root = getContainer();
  const backdrop = document.createElement("div");
  backdrop.className = BACKDROP_CLASS;

  const box = document.createElement("div");
  box.className = BOX_CLASS;
  box.innerHTML = `
    <div class="${HEADER_CLASS}">
      <h2 class="${TITLE_CLASS}">${escapeHtml(titleText)}</h2>
      <button type="button" class="${CLOSE_CLASS}" data-dpr-modal-close aria-label="${escapeHtml(closeLabel)}">&times;</button>
    </div>
    <div class="${BODY_CLASS}">${escapeHtml(message)}</div>
    <div class="${FOOTER_CLASS}">
      <button type="button" class="${BTN_CLASS}" data-dpr-modal-cancel>${escapeHtml(cancelLabel)}</button>
      <button type="button" class="${BTN_CLASS} ${BTN_PRIMARY_CLASS}" data-dpr-modal-ok>${escapeHtml(okLabel)}</button>
    </div>
  `;

  backdrop.appendChild(box);
  root.appendChild(backdrop);

  return new Promise((resolve) => {
    const close = (value: boolean) => (): void => {
      backdrop.remove();
      resolve(value);
    };
    box.querySelector("[data-dpr-modal-ok]")?.addEventListener("click", close(true));
    box.querySelector("[data-dpr-modal-cancel]")?.addEventListener("click", close(false));
    box.querySelector("[data-dpr-modal-close]")?.addEventListener("click", close(false));
    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop) close(false)();
    });
    box.addEventListener("click", (e) => e.stopPropagation());
  });
}

export const Modal = { alert, confirm };
